/**
 * PROTOTYPE shared model  --  THROWAWAY.
 *
 * Question: what input UI best captures everything the four new modules
 * (Material Traceability, PipeFitter, NDT, Coating) need for pricing?
 *
 * This file holds the shared state shape, mock seed data, the CRC palette,
 * and a simplified price estimate so all three layout variants show the same
 * fields and a live total. Delete once a layout wins.
 */

export const CRC = {
  heroRed: '#EB1C37',
  heroRed10: '#FBD2D7',
  heroRed20: '#F8B3BC',
  blueprintBlue: '#00273F',
  heritageGrey: '#969696',
  textGrey: '#464646',
  green: '#1F9A3A',
  yellow: '#FFCA57',
  pageBg: '#F5F6F8',
  border: '#E2E5EA',
  rowDivider: '#EEF0F3',
  readableRed: '#C4001F',
};

export type Difficulty = 'native_digital' | 'api' | 'spreadsheet' | 'scanned_pdf' | 'paper';

export const DIFFICULTY: { key: Difficulty; label: string; mult: number }[] = [
  { key: 'native_digital', label: 'Native digital', mult: 0.8 },
  { key: 'api', label: 'API', mult: 1.0 },
  { key: 'spreadsheet', label: 'Spreadsheet / CSV', mult: 1.5 },
  { key: 'scanned_pdf', label: 'Scanned PDF', mult: 2.5 },
  { key: 'paper', label: 'Handwritten paper', mult: 4.0 },
];

export type ModuleKey = 'materialTraceability' | 'pipeFitter' | 'ndt' | 'coating';
export type CapturePath = 'A' | 'B' | 'C';

export const CAPTURE_PATH_LABELS: Record<CapturePath, string> = {
  A: 'A — CRCE service crew',
  B: 'B — Instrumented bolt-on',
  C: 'C — Contractor BYOD upload',
};

/** Data-type checklists drive the NRE estimator (more types = higher onboarding). */
export const DATA_TYPES: Record<ModuleKey, string[]> = {
  materialTraceability: ['MTR', 'Chemical', 'Tensile', 'CVN Impact', 'AUT', 'Hydrostatic', 'Dimensional', 'Hardness', 'Coating'],
  ndt: ['RT', 'UT manual', 'PAUT', 'TOFD', 'MT', 'PT', 'VT', 'ECA reports'],
  pipeFitter: ['Scan point cloud', 'Fit-up report', 'Bevel geometry'],
  coating: ['Holiday detection', 'Cure / CDT', 'DFT readings', 'Visual'],
};

export interface SourceRow {
  id: string;
  name: string;
  difficulty: Difficulty;
  dataTypes: string[];
  nreOverride?: number;
  /** PipeFitter + Coating attach a capture path per contractor row. */
  capturePath?: CapturePath;
  serviceDays?: number;     // path A
  boltonMachines?: number;  // path B
}

export interface ModuleState {
  key: ModuleKey;
  name: string;
  rowNoun: string;          // mill / vendor / contractor
  enabled: boolean;
  volume: number;
  volumeLabel: string;
  sources: SourceRow[];
  hasCapturePath: boolean;  // PF + Coating show per-row path columns
  // module-specific extras
  mtAddons?: { gas: boolean; wire: boolean; coatingMaterials: boolean };
  mtPipeEndScanDays?: number;
  pfScanningMode?: 'none' | 'byod' | 'crew';
  pfScanningCrewDays?: number;
  pfReporting?: boolean;
  pfAlgorithm?: boolean;
}

export interface ProjectState {
  durationMonths: number;
  modules: ModuleState[];
}

// ── Rates (mirror the prototype engine defaults) ───────────────────────────────
const RATES = {
  duration_discount: (m: number) => (m <= 12 ? 0 : m <= 24 ? 5 : m <= 36 ? 10 : m <= 60 ? 15 : 20),
  nreBase: 2000,
  nrePerType: 500,
  bolton: { unitCost: 4000, util: 0.6, margin: 0.6, rental: 0.1 },
  materialTraceability: { platform: 1500, perItem: 12, addon: 1000, scanDay: 1200 },
  pipeFitter: { platform: 1500, reporting: 8, algorithm: 15, crewDay: 1500 },
  ndt: { platform: 1500, perItem: 20 },
  coating: { platform: 1500, perItem: 10, serviceDay: 1400 },
};

function disc(amount: number, months: number) {
  return amount * (1 - RATES.duration_discount(months) / 100);
}
function nreFor(s: SourceRow) {
  if (s.nreOverride != null) return s.nreOverride;
  const mult = DIFFICULTY.find((d) => d.key === s.difficulty)?.mult ?? 1;
  return (RATES.nreBase + RATES.nrePerType * s.dataTypes.length) * mult;
}
function boltonCost(machines: number, months: number) {
  const b = RATES.bolton;
  const perUnit = (b.unitCost + b.unitCost * b.margin) / b.util;
  return perUnit * b.rental * machines * months;
}

export interface Estimate { oneTime: number; recurring: number; total: number }

export function estimateModule(m: ModuleState, months: number): Estimate {
  if (!m.enabled) return { oneTime: 0, recurring: 0, total: 0 };
  let oneTime = 0;
  let recurring = 0;
  const nre = m.sources.reduce((sum, s) => sum + nreFor(s), 0);

  if (m.key === 'materialTraceability') {
    const r = RATES.materialTraceability;
    oneTime += nre + disc(r.perItem * m.volume, months);
    if (m.mtAddons?.gas) oneTime += r.addon;
    if (m.mtAddons?.wire) oneTime += r.addon;
    if (m.mtAddons?.coatingMaterials) oneTime += r.addon * 1.5;
    oneTime += r.scanDay * (m.mtPipeEndScanDays ?? 0);
    recurring += disc(r.platform * months, months);
  } else if (m.key === 'ndt') {
    const r = RATES.ndt;
    oneTime += nre + disc(r.perItem * m.volume, months);
    recurring += disc(r.platform * months, months);
  } else if (m.key === 'pipeFitter') {
    const r = RATES.pipeFitter;
    if (m.pfScanningMode === 'byod') oneTime += nre;
    if (m.pfScanningMode === 'crew') oneTime += r.crewDay * (m.pfScanningCrewDays ?? 0);
    if (m.pfReporting) oneTime += disc(r.reporting * m.volume, months);
    if (m.pfAlgorithm) oneTime += disc(r.algorithm * m.volume, months);
    recurring += disc(r.platform * months, months);
    const bolt = m.sources.reduce((s, src) => s + boltonCost(src.boltonMachines ?? 0, months), 0);
    recurring += bolt;
  } else if (m.key === 'coating') {
    const r = RATES.coating;
    oneTime += disc(r.perItem * m.volume, months);
    m.sources.forEach((s) => {
      if (s.capturePath === 'A') oneTime += r.serviceDay * (s.serviceDays ?? 0);
      if (s.capturePath === 'B') recurring += boltonCost(s.boltonMachines ?? 0, months);
    });
    recurring += disc(r.platform * months, months);
  }
  return { oneTime, recurring, total: oneTime + recurring };
}

export function estimateProject(p: ProjectState): Estimate {
  return p.modules.reduce(
    (acc, m) => {
      const e = estimateModule(m, p.durationMonths);
      return { oneTime: acc.oneTime + e.oneTime, recurring: acc.recurring + e.recurring, total: acc.total + e.total };
    },
    { oneTime: 0, recurring: 0, total: 0 }
  );
}

export const money = (x: number) =>
  `$${Math.round(x).toLocaleString('en-US')}`;

// ── Seed state ─────────────────────────────────────────────────────────────────
export const initialProject: ProjectState = {
  durationMonths: 36,
  modules: [
    {
      key: 'materialTraceability', name: 'Material Traceability', rowNoun: 'Mill',
      enabled: true, volume: 5000, volumeLabel: 'Total pipes', hasCapturePath: false,
      sources: [
        { id: 'm1', name: 'Tenaris (Argentina)', difficulty: 'spreadsheet', dataTypes: ['MTR', 'Chemical', 'Tensile', 'CVN Impact'] },
        { id: 'm2', name: 'JSW (India)', difficulty: 'scanned_pdf', dataTypes: ['MTR', 'Chemical'] },
      ],
      mtAddons: { gas: false, wire: false, coatingMaterials: false }, mtPipeEndScanDays: 0,
    },
    {
      key: 'pipeFitter', name: 'PipeFitter', rowNoun: 'Fitting contractor',
      enabled: false, volume: 4000, volumeLabel: 'Expected fit-ups', hasCapturePath: true,
      sources: [{ id: 'f1', name: 'Saipem', difficulty: 'api', dataTypes: ['Scan point cloud'], capturePath: 'C', serviceDays: 0, boltonMachines: 0 }],
      pfScanningMode: 'byod', pfScanningCrewDays: 20, pfReporting: true, pfAlgorithm: true,
    },
    {
      key: 'ndt', name: 'NDT', rowNoun: 'NDT vendor',
      enabled: false, volume: 3000, volumeLabel: 'Inspection volume', hasCapturePath: false,
      sources: [{ id: 'n1', name: 'Applus+', difficulty: 'scanned_pdf', dataTypes: ['RT', 'UT manual', 'PAUT'] }],
    },
    {
      key: 'coating', name: 'Coating', rowNoun: 'Coating contractor',
      enabled: false, volume: 4000, volumeLabel: 'Joints coated', hasCapturePath: true,
      sources: [{ id: 'c1', name: 'Bredero Shaw', difficulty: 'api', dataTypes: ['DFT readings', 'Holiday detection'], capturePath: 'A', serviceDays: 15, boltonMachines: 0 }],
    },
  ],
};
